import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { validateEmail } from '../../utility';
import InputMask from 'react-input-mask';
import DeactivateModal from '../../components/company/deactivate-modal';
import { createContacts, editContacts, deactivateContacts } from '../../services/contacts';
import { getUnitBedsService } from '../../services/unit-beds';
import { getCompanyUnit } from '../../services/company';
import { isResponseOk } from '../../utility/isResponseOk';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import { genderOptions, stateOptions } from '../../constants';
import DateCalendar from '../calendars/DateCalendar/DateCalendar';

function UserModal({
  isOpen,
  companyId,
  closeModal,
  companyName,
  userData,
  handelCreateUser,
  facilities,
  handleDeactivateResident,
  showDeactivate = false,
}) {
  const [errorUser, setErrorUser] = useState({});

  const [facilityOptions, setFacilityOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

  const [user, setUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    unit_bed: '',
    facility_id: '',
    unit_id: '',
    representatives: [],
    deleted_at: null,
    gender: null,
    date_of_birth: null,
    ssn: '',
    address: '',
    address_2: '',
    city: '',
    state: null,
    zip_code: '',
    physician_name: '',
    medical_record_number: '',
    date_of_admission: null,
  });

  useEffect(() => {
    if (userData) {
      const newState = userData.representatives ? [...userData.representatives] : [];
      setUser({
        ...userData,
        facility_id: userData.facility ? { value: userData.facility.id, label: userData.facility.name } : '',
        unit_id: userData.unit ? { value: userData.unit.id, label: userData.unit.name } : '',
        unit_bed: userData.unit_bed ? { value: userData.unit_bed.id, label: userData.unit_bed.bed } : null,
        gender: userData.gender ? genderOptions.find((item) => item.value === userData.gender) : null,
        state: userData.state ? stateOptions.find((item) => item.value === userData.state) : null,
        representatives: [...newState],
      });
    }
  }, [userData, isOpen]);

  const getUnitBeds = useCallback(async () => {
    if (!user.unit_id) {
      setBedOptions(null);
    } else {
      try {
        const params = {
          get_all: true,
          is_free: true,
          for_resident: userData?.id,
        };
        const response = await getUnitBedsService(user.unit_id?.value, params);

        if (isResponseOk(response)) {
          setBedOptions(response.data.map((item) => ({ value: item.id, label: item.bed })));
        }
      } catch (e) {
        console.log(e);
      }
    }
  }, [user.unit_id]);

  useEffect(() => {
    getUnitBeds();
  }, [getUnitBeds]);

  useEffect(() => {
    if (user.facility_id && user.facility_id.value) {
      getCompanyUnit(companyId, 'getAll', user.facility_id.value)
        .then((res) => {
          setUnitOptions(res.map((item) => ({ value: item.id, label: item.name })));
        })
        .catch((e) => console.log(e));
    } else {
      setUnitOptions([]);
    }
    // eslint-disable-next-line
  }, [companyId, isOpen, user]);

  useEffect(() => {
    setFacilityOptions(
      facilities
        .filter((item) => !item.deleted_at && !item.is_tabula_sync_on)
        .map((item) => ({ value: item.id, label: item.name, is_tabula_sync_on: item.is_tabula_sync_on })),
    );
    // eslint-disable-next-line
  }, [facilities, isOpen]);

  useEffect(() => {
    if (bedOptions?.map((item) => item.value).indexOf(user.unit_bed?.value) == -1) {
      setUser((state) => ({ ...state, unit_bed: null }));
    }
  }, [bedOptions]);

  const handleChange = (value, key) => {
    setUser({
      ...user,
      [key]: value,
    });

    if (key === 'facility_id' && value !== user.facility_id) {
      setUser({
        ...user,
        [key]: value,
        unit_id: '',
      });
    }

    if (key === 'email' && value) {
      if (validateEmail(value)) {
        delete errorUser[key];
      }
      return;
    }

    if (errorUser.hasOwnProperty(key)) {
      delete errorUser[key];
    }
  };

  const handleChangeRepresentative = (value, key, index) => {
    setUser((prevState) => {
      let newState = [...user.representatives];
      newState[index][key] = value;
      return {
        ...user,
        representatives: [...newState],
      };
    });
  };

  const resetForm = () => {
    setUser({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      unit_bed: null,
      facility_id: '',
      unit_id: '',
      representatives: [],
    });
    setErrorUser({});
  };

  const handleSubmit = () => {
    const formDate = {
      ...user,
      facility_id: user.facility_id?.value,
      unit_id: user.unit_id?.value,
      unit_bed: user.unit_bed?.value,
      gender: user.gender?.value,
      date_of_birth: user.date_of_birth ? dayjs(user.date_of_birth).format('YYYY-MM-DD') : null,
      state: user.state?.value,
      date_of_admission: user.date_of_admission ? dayjs(user.date_of_admission).format('YYYY-MM-DD') : null,
    };

    if (userData?.id) {
      delete formDate.id;
      delete formDate.company;
      delete formDate.facility;
      delete formDate.facility_id;
      delete formDate.unit;
      delete formDate.deleted_at;
      editContacts(formDate, userData.id)
        .then((res) => {
          resetForm();
          handelCreateUser(res);
        })
        .catch((e) => {
          if (e?.response?.data) {
            setErrorUser(JSON.parse(JSON.stringify(e.response.data)));
          }
        });
    } else {
      createContacts(formDate)
        .then((res) => {
          resetForm();
          handelCreateUser(res);
        })
        .catch((e) => {
          if (e?.response?.data) {
            setErrorUser(JSON.parse(JSON.stringify(e.response.data)));
          }
        });
    }
  };
  const handelCloseModal = () => {
    closeModal();
    resetForm();
  };

  const addMoreRepresentative = () => {
    setUser({
      ...user,
      representatives: [
        ...user.representatives,
        {
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          relation_with_resident: null,
        },
      ],
    });
  };
  const removeRepresentative = (index) => {
    let newState = [...user.representatives];
    newState.splice(index, 1);
    setUser({
      ...user,
      representatives: [...newState],
    });
  };

  const handleDeactivate = async () => {
    try {
      await deactivateContacts(userData.id);
      handleDeactivateResident();
      setIsDeactivateModalOpen(false);
    } catch (e) {
      console.log(e);
    }
  };

  const canEditSyncFields = useMemo(() => {
    return user.facility?.is_tabula_sync_on !== true;
  }, [user.facility]);

  return (
    <>
      <Modal className="modal-default" isOpen={isOpen} onRequestClose={handelCloseModal} contentLabel="Example Modal" ariaHideApp={false}>
        <h2 className="modal-header">
          {userData?.id ? 'Edit' : 'Add New'} Resident{' '}
          <button className="btn btn-icon" onClick={handelCloseModal}>
            <i className="icon-icon-x"></i>
          </button>
        </h2>
        {!!companyName && <h3 className="modal-subheader">for {companyName} company</h3>}
        <div className="form-wrapper">
          <div className="d-flex justify-content-space-between" style={{ width: '100%' }}>
            <h3 className="form-wrapper-title" style={userData?.id ? { flex: 'unset', marginBottom: '20px' } : {}}>
              <img src="/images/company-add-icon-3.svg" alt="" />
              Resident information
            </h3>
            {!!userData?.id && showDeactivate && !userData.deleted_at && canEditSyncFields && (
              <button className="btn btn-outline btn-danger" onClick={() => setIsDeactivateModalOpen(true)}>
                <svg className="mr-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <g clipPath="url(#clip0_1495_6510)">
                    <path
                      d="M12.5001 7.50008L7.50008 12.5001M7.50008 7.50008L12.5001 12.5001M18.3334 10.0001C18.3334 14.6025 14.6025 18.3334 10.0001 18.3334C5.39771 18.3334 1.66675 14.6025 1.66675 10.0001C1.66675 5.39771 5.39771 1.66675 10.0001 1.66675C14.6025 1.66675 18.3334 5.39771 18.3334 10.0001Z"
                      stroke="white"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </g>
                  <defs>
                    <clipPath id="clip0_1495_6510">
                      <rect width="20" height="20" fill="white"></rect>
                    </clipPath>
                  </defs>
                </svg>
                Deactivate
              </button>
            )}
          </div>

          <div className={`form-item form-item-half ${errorUser.first_name ? 'form-item-error' : ''}`}>
            <label className="form-label">Resident Name</label>
            <input
              disabled={!canEditSyncFields}
              placeholder="Enter First Name"
              value={user.first_name}
              onChange={(e) => handleChange(e.target.value, 'first_name')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.first_name && errorUser.first_name[0]}</span>
          </div>
          <div className={`form-item form-item-half ${errorUser.last_name ? 'form-item-error' : ''}`}>
            <label className="form-label">Resident Last Name</label>
            <input
              disabled={!canEditSyncFields}
              placeholder="Enter Last Name"
              value={user.last_name}
              onChange={(e) => handleChange(e.target.value, 'last_name')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.last_name && errorUser.last_name[0]}</span>
          </div>

          <div className={`form-item form-item-half ${errorUser.gender ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Gender <span className="optional">Optional</span>
            </label>
            <Select
              options={genderOptions}
              className="form-control-select"
              classNamePrefix="react-select"
              value={user.gender}
              placeholder="Select"
              onChange={(e) => handleChange(e, 'gender')}
              isSearchable={false}
            ></Select>
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.gender && errorUser.gender[0]}</span>
          </div>

          <div className={`form-item form-item-half ${errorUser.date_of_birth ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Date of birth <span className="optional">Optional</span>
            </label>
            <DateCalendar
              selectsRange={false}
              isClearable
              startYear={1920}
              yearsRange={110}
              handleChange={(date) => handleChange(date, 'date_of_birth')}
              maxDate={new Date()}
              placeholderText="Select Date"
              selectedDate={user.date_of_birth}
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.date_of_birth && errorUser.date_of_birth[0]}</span>
          </div>

          <div className={`form-item  ${errorUser.ssn ? 'form-item-error' : ''}`}>
            <label className="form-label">
              SSN <span className="optional">Optional</span>
            </label>
            <input
              placeholder="Enter SSN"
              value={user.ssn}
              onChange={(e) => handleChange(e.target.value, 'ssn')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.ssn && errorUser.ssn[0]}</span>
          </div>
        </div>

        <div className="form-wrapper">
          <div className="d-flex justify-content-space-between" style={{ width: '100%' }}>
            <h3 className="form-wrapper-title" style={userData?.id ? { flex: 'unset', marginBottom: '20px' } : {}}>
              <svg width="37" height="36" viewBox="0 0 37 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2.5" y="2" width="32" height="32" rx="16" fill="#F4EBFF" />
                <rect x="2.5" y="2" width="32" height="32" rx="16" stroke="#F9F5FF" strokeWidth="4" />
                <g clipPath="url(#clip0_14911_54483)">
                  <path
                    d="M25.1663 14.0003C25.1663 13.267 24.5663 12.667 23.833 12.667H13.1663C12.433 12.667 11.833 13.267 11.833 14.0003M25.1663 14.0003V22.0003C25.1663 22.7337 24.5663 23.3337 23.833 23.3337H13.1663C12.433 23.3337 11.833 22.7337 11.833 22.0003V14.0003M25.1663 14.0003L18.4997 18.667L11.833 14.0003"
                    stroke="#7F56D9"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_14911_54483">
                    <rect width="16" height="16" fill="white" transform="translate(10.5 10)" />
                  </clipPath>
                </defs>
              </svg>
              Contact Details
            </h3>
          </div>

          <div className={`form-item form-item-half ${errorUser.phone ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Phone Number <span className="optional">Optional</span>
            </label>
            <InputMask
              placeholder="XXX-XXX-XXXX"
              mask="999-999-9999"
              value={user.phone}
              onChange={(e) => handleChange(e.target.value, 'phone')}
              className="form-control"
            ></InputMask>

            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.phone && errorUser.phone[0]}</span>
          </div>
          <div className={`form-item form-item-half form-item-mail ${errorUser.email ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Email <span className="optional">Optional</span>
            </label>
            <img className="mail-icon" src="/images/mail-icon.svg" alt="" />
            <input
              placeholder="mail@example.com"
              id="email"
              value={user.email}
              onChange={(e) => handleChange(e.target.value, 'email')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.email && errorUser.email[0]}</span>
          </div>

          <div className={`form-item form-item-half ${errorUser.address ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Address 1 <span className="optional">Optional</span>
            </label>
            <input
              placeholder="Enter Address"
              value={user.address}
              onChange={(e) => handleChange(e.target.value, 'address')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.address && errorUser.address[0]}</span>
          </div>
          <div className={`form-item form-item-half ${errorUser.address_2 ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Address 2 <span className="optional">Optional</span>
            </label>
            <input
              placeholder="Enter Address"
              value={user.address_2}
              onChange={(e) => handleChange(e.target.value, 'address_2')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.address_2 && errorUser.address_2[0]}</span>
          </div>

          <div className="form-item city-state-zip">
            <div className={`form-item ${errorUser.city ? 'form-item-error' : ''}`}>
              <label className="form-label">
                City <span className="optional">Optional</span>
              </label>
              <input
                placeholder="Enter city"
                value={user.city}
                onChange={(e) => handleChange(e.target.value, 'city')}
                type="text"
                className="form-control"
              />
              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">{errorUser.city && errorUser.city[0]}</span>
            </div>

            <div className={`form-item state-form-item ${errorUser.state ? 'form-item-error' : ''}`}>
              <label className="form-label">
                State <span className="optional">Optional</span>
              </label>
              <Select
                options={stateOptions}
                className="form-control-select"
                classNamePrefix="react-select"
                value={user.state}
                placeholder="Select"
                onChange={(e) => handleChange(e, 'state')}
              ></Select>
              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">{errorUser.state && errorUser.state[0]}</span>
            </div>

            <div className={`form-item zip-code-form-item ${errorUser.zip_code ? 'form-item-error' : ''}`}>
              <label className="form-label">
                ZIP Code <span className="optional">Optional</span>
              </label>
              <input
                placeholder="Enter"
                value={user.zip_code}
                onChange={(e) => handleChange(e.target.value, 'zip_code')}
                type="text"
                className="form-control"
              />
              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">{errorUser.zip_code && errorUser.zip_code[0]}</span>
            </div>
          </div>
        </div>

        <div className="form-wrapper">
          <div className="d-flex justify-content-space-between" style={{ width: '100%' }}>
            <h3 className="form-wrapper-title" style={userData?.id ? { flex: 'unset', marginBottom: '20px' } : {}}>
              <svg width="37" height="36" viewBox="0 0 37 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2.5" y="2" width="32" height="32" rx="16" fill="#F4EBFF" />
                <rect x="2.5" y="2" width="32" height="32" rx="16" stroke="#F9F5FF" strokeWidth="4" />
                <path
                  d="M21.167 12.6663H22.5003C22.8539 12.6663 23.1931 12.8068 23.4431 13.0569C23.6932 13.3069 23.8337 13.6461 23.8337 13.9997V23.333C23.8337 23.6866 23.6932 24.0258 23.4431 24.2758C23.1931 24.5259 22.8539 24.6663 22.5003 24.6663H14.5003C14.1467 24.6663 13.8076 24.5259 13.5575 24.2758C13.3075 24.0258 13.167 23.6866 13.167 23.333V13.9997C13.167 13.6461 13.3075 13.3069 13.5575 13.0569C13.8076 12.8068 14.1467 12.6663 14.5003 12.6663H15.8337M16.5003 11.333H20.5003C20.8685 11.333 21.167 11.6315 21.167 11.9997V13.333C21.167 13.7012 20.8685 13.9997 20.5003 13.9997H16.5003C16.1321 13.9997 15.8337 13.7012 15.8337 13.333V11.9997C15.8337 11.6315 16.1321 11.333 16.5003 11.333Z"
                  stroke="#7F56D9"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Care Details
            </h3>
          </div>

          <div className={`form-item form-item-select ${errorUser.facility_id ? 'form-item-error' : ''}`}>
            <label className="form-label">Facility</label>
            <Select
              isDisabled={!facilityOptions.length}
              options={facilityOptions}
              className="form-control-select"
              classNamePrefix="react-select"
              value={user.facility_id}
              placeholder="Select Facility"
              onChange={(e) => handleChange(e, 'facility_id')}
            ></Select>
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.facility_id && errorUser.facility_id[0]}</span>
          </div>

          <div className={`form-item form-item-half form-item-select ${errorUser.unit_id ? 'form-item-error' : ''}`}>
            <label className="form-label">Unit</label>
            <Select
              isDisabled={!unitOptions.length}
              options={unitOptions}
              className="form-control-select"
              classNamePrefix="react-select"
              value={user.unit_id}
              placeholder="Select Unit"
              onChange={(e) => handleChange(e, 'unit_id')}
            ></Select>
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.unit_id && errorUser.unit_id[0]}</span>
          </div>
          <div className={`form-item form-item-half form-item-select ${errorUser.unit_bed ? 'form-item-error' : ''}`}>
            <label className="form-label">Room number + Bed</label>
            <Select
              isDisabled={!bedOptions?.length || !canEditSyncFields}
              options={bedOptions}
              className="form-control-select"
              classNamePrefix="react-select"
              value={user.unit_bed}
              placeholder="Select Bed"
              onChange={(e) => handleChange(e, 'unit_bed')}
            ></Select>
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.unit_bed && errorUser.unit_bed[0]}</span>
          </div>

          <div className={`form-item form-item-half ${errorUser.physician_name ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Physician Name <span className="optional">Optional</span>
            </label>
            <input
              placeholder="Enter name last name"
              value={user.physician_name}
              onChange={(e) => handleChange(e.target.value, 'physician_name')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.physician_name && errorUser.physician_name[0]}</span>
          </div>

          <div className={`form-item form-item-half ${errorUser.date_of_admission ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Date of Admission <span className="optional">Optional</span>
            </label>
            <DateCalendar
              selectedDate={user.date_of_admission}
              isClearable
              handleChange={(date) => handleChange(date, 'date_of_admission')}
              maxDate={new Date()}
              placeholderText={'Select date'}
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.date_of_admission && errorUser.date_of_admission[0]}</span>
          </div>

          <div className={`form-item ${errorUser.medical_record_number ? 'form-item-error' : ''}`}>
            <label className="form-label">
              Medical Record Number <span className="optional">Optional</span>
            </label>
            <input
              placeholder="Enter medical record number"
              value={user.medical_record_number}
              onChange={(e) => handleChange(e.target.value, 'medical_record_number')}
              type="text"
              className="form-control"
            />
            <img className="danger-icon" src="/images/red-alert.svg" alt="" />
            <span className="form-error">{errorUser.medical_record_number && errorUser.medical_record_number[0]}</span>
          </div>
        </div>

        {errorUser.non_field_errors && (
          <div className="form-error mb-4">
            <img className="mr-2" src="/images/red-alert.svg" alt="" /> {errorUser.non_field_errors[0]}
          </div>
        )}

        {user.representatives.map((item, index) => (
          <div key={index} className="form-wrapper">
            <h3 className="form-wrapper-title">
              <img src="/images/company-add-icon-3.svg" alt="" />
              Representative information{' '}
              <button className="btn btn-icon mr-a" onClick={() => removeRepresentative(index)}>
                <i className="icon-remove"></i>
              </button>
            </h3>
            <div
              className={`form-item form-item-half ${errorUser.representatives && errorUser.representatives[index].first_name ? 'form-item-error' : ''}`}
            >
              <label className="form-label">Representative Name</label>
              <input
                placeholder="Enter First Name"
                value={item.first_name}
                onChange={(e) => handleChangeRepresentative(e.target.value, 'first_name', index)}
                type="text"
                className="form-control"
              />
              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">
                {errorUser.representatives &&
                  errorUser.representatives[index]?.first_name &&
                  errorUser.representatives[index].first_name[0]}
              </span>
            </div>
            <div
              className={`form-item form-item-half ${errorUser.representatives && errorUser.representatives[index]?.last_name ? 'form-item-error' : ''}`}
            >
              <label className="form-label">Representative Last Name</label>
              <input
                placeholder="Enter Last Name"
                value={item.last_name}
                onChange={(e) => handleChangeRepresentative(e.target.value, 'last_name', index)}
                type="text"
                className="form-control"
              />
              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">
                {errorUser.representatives && errorUser.representatives[index]?.last_name && errorUser.representatives[index].last_name[0]}
              </span>
            </div>
            <div
              className={`form-item form-item-half form-item-mail ${errorUser.representatives && errorUser.representatives[index]?.email ? 'form-item-error' : ''}`}
            >
              <label className="form-label">
                Email <span className="optional">Optional</span>
              </label>
              <img className="mail-icon" src="/images/mail-icon.svg" alt="" />
              <input
                placeholder="mail@example.com"
                id="email"
                value={item.email}
                onChange={(e) => handleChangeRepresentative(e.target.value, 'email', index)}
                type="text"
                className="form-control"
              />
              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">
                {errorUser.representatives && errorUser.representatives[index]?.email && errorUser.representatives[index].email[0]}
              </span>
            </div>
            <div
              className={`form-item form-item-half ${errorUser.representatives && errorUser.representatives[index]?.phone ? 'form-item-error' : ''}`}
            >
              <label className="form-label">
                Phone Number <span className="optional">Optional</span>
              </label>
              <InputMask
                placeholder="XXX-XXX-XXXX"
                mask="999-999-9999"
                value={item.phone}
                onChange={(e) => handleChangeRepresentative(e.target.value, 'phone', index)}
                className="form-control"
              ></InputMask>

              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">
                {errorUser.representatives && errorUser.representatives[index]?.phone && errorUser.representatives[index].phone[0]}
              </span>
            </div>

            <div
              className={`form-item form-item-select ${errorUser.representatives && errorUser.representatives[index]?.relation_with_resident ? 'form-item-error' : ''}`}
            >
              <label className="form-label">
                Relation with Resident <span className="optional">Optional</span>
              </label>
              <input
                placeholder="Enter Relation"
                value={item.relation_with_resident}
                onChange={(e) => handleChangeRepresentative(e.target.value, 'relation_with_resident', index)}
                type="text"
                className="form-control"
              />

              <img className="danger-icon" src="/images/red-alert.svg" alt="" />
              <span className="form-error">
                {errorUser.representatives &&
                  errorUser.representatives[index]?.relation_with_resident &&
                  errorUser.representatives[index].relation_with_resident[0]}
              </span>
            </div>
          </div>
        ))}

        {user.representatives.length < 2 && (
          <span className="add-facility cursor-pointer" onClick={() => addMoreRepresentative()}>
            <i className="icon-plus-svgrepo-com"></i> Add Representative
          </span>
        )}

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={handelCloseModal}>
            Cancel
          </button>
          <button className="btn" onClick={handleSubmit}>
            Save
          </button>
        </div>
      </Modal>
      {isDeactivateModalOpen && (
        <DeactivateModal
          isOpen
          closeModal={() => setIsDeactivateModalOpen(false)}
          handelSuccess={handleDeactivate}
          title="Resident Profile Deactivation"
          subtitle="Deactivation will temporarily hide this resident from the system. You can reactivate it anytime in this resident profile."
        />
      )}
    </>
  );
}

export default UserModal;
